// Main Application - Production Optimized with Existing Modules

// Import CSS
import '../css/main.css';

import { domainContent, siteConfig } from './config.js';
import { AnalyticsEngine } from './modules/analytics.js';
import { ContactForm } from './modules/contact-form.js';
import { SecurityManager } from './modules/security.js';
import { PerformanceMonitor } from './modules/performance.js';

class ModernSite {
    constructor() {
        this.currentDomain = window.location.hostname.toLowerCase();
        this.config = domainContent[this.currentDomain] || domainContent['default'];
        this.analytics = new AnalyticsEngine();
        this.security = new SecurityManager();
        this.performance = new PerformanceMonitor();
        this.contactForm = new ContactForm();
        this.init();
    }

    async init() {
        try {
            // Security first
            this.security.init();
            
            // Core initialization
            this.setupDOMElements();
            await this.initializeAnimations();
            this.setupEventListeners();
            this.initializeParticles();
            this.setupIntersectionObservers();
            this.initializeDynamicContent();
            this.initializeCookieConsent();
            
            // Initialize modules
            this.contactForm.init(this.config);
            
            // Performance monitoring
            this.performance.init();
            
            // Remove preloader after initialization
            this.removePreloader();
            
            // Track initial page view
            await this.analytics.trackPageView();
            
        } catch (error) {
            console.error('Initialization error:', error);
            this.handleInitError(error);
        }
    }

    removePreloader() {
        const preloader = document.getElementById('preloader');
        if (preloader) {
            // Ensure minimum loading time for smooth UX
            const minLoadTime = 500;
            const loadStartTime = window.performance.timing.navigationStart;
            const currentTime = Date.now();
            const elapsedTime = currentTime - loadStartTime;
            const remainingTime = Math.max(0, minLoadTime - elapsedTime);
            
            setTimeout(() => {
                preloader.classList.add('loaded');
                // Remove from DOM after animation
                setTimeout(() => preloader.remove(), 500);
            }, remainingTime);
        }
    }

    setupDOMElements() {
        // Update page content based on domain config
        document.title = this.config.title;
        
        // Update meta tags
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.content = this.config.subtitle;
        }
        
        const elements = {
            logoText: document.getElementById('logoText'),
            heroTitle: document.querySelector('.hero-title'),
            heroSubtitle: document.querySelector('.hero-subtitle'),
            pageTitle: document.getElementById('pageTitle')
        };
        
        if (elements.pageTitle) elements.pageTitle.textContent = this.config.title;
        if (elements.logoText) elements.logoText.textContent = this.config.title.split(' ')[0];
        
        // Update hero content with animation
        if (elements.heroTitle) {
            const words = this.config.title.split(' ');
            elements.heroTitle.innerHTML = `
                <span class="title-line">
                    ${words.slice(0, 2).map((word, i) => 
                        `<span class="title-word" style="animation-delay: ${i * 0.1}s">${this.escapeHtml(word)}</span>`
                    ).join(' ')}
                </span>
                <span class="title-line">
                    ${words.slice(2).map((word, i) => 
                        `<span class="title-word" style="animation-delay: ${(i + 2) * 0.1}s">${this.escapeHtml(word)}</span>`
                    ).join(' ')}
                </span>
            `;
        }
        
        if (elements.heroSubtitle) {
            elements.heroSubtitle.textContent = this.config.subtitle;
        }
        
        // Set domain-specific data attributes
        document.body.setAttribute('data-domain', this.currentDomain);
        document.body.setAttribute('data-theme', this.config.theme || 'default');
    }

    escapeHtml(text) {
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        };
        return text.replace(/[&<>"']/g, m => map[m]);
    }

    async initializeAnimations() {
        // Number counter animation with improved performance
        const observerOptions = {
            threshold: 0.5,
            rootMargin: '0px'
        };
        
        const numberObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const finalValue = parseInt(target.getAttribute('data-count'));
                    requestAnimationFrame(() => this.animateNumber(target, finalValue));
                    numberObserver.unobserve(target);
                }
            });
        }, observerOptions);
        
        document.querySelectorAll('.stat-number').forEach(el => {
            numberObserver.observe(el);
        });
    }

    animateNumber(element, finalValue) {
        const duration = 2000;
        const frameDuration = 1000 / 60; // 60fps
        const totalFrames = Math.round(duration / frameDuration);
        let frame = 0;
        
        const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
        
        const animate = () => {
            frame++;
            const progress = easeOutQuart(frame / totalFrames);
            const currentValue = Math.round(finalValue * progress);
            
            element.textContent = currentValue.toLocaleString();
            
            if (frame < totalFrames) {
                requestAnimationFrame(animate);
            }
        };
        
        requestAnimationFrame(animate);
    }

    setupEventListeners() {
        // Debounced scroll handler
        let scrollTimeout;
        let lastScroll = 0;
        const navbar = document.getElementById('navbar');
        const backToTop = document.getElementById('backToTop');
        
        const handleScroll = () => {
            const currentScroll = window.pageYOffset;
            
            // Navbar visibility
            if (navbar) {
                if (currentScroll > 50) {
                    navbar.classList.add('scrolled');
                } else {
                    navbar.classList.remove('scrolled');
                }
                
                // Auto-hide on scroll down, show on scroll up
                if (currentScroll > lastScroll && currentScroll > 300) {
                    navbar.style.transform = 'translateY(-100%)';
                } else {
                    navbar.style.transform = 'translateY(0)';
                }
            }
            
            // Back to top visibility
            if (backToTop) {
                if (currentScroll > 500) {
                    backToTop.classList.add('visible');
                } else {
                    backToTop.classList.remove('visible');
                }
            }
            
            lastScroll = currentScroll;
        };
        
        window.addEventListener('scroll', () => {
            if (!scrollTimeout) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    scrollTimeout = null;
                });
                scrollTimeout = setTimeout(() => scrollTimeout = null, 100);
            }
        }, { passive: true });
        
        // Smooth scroll for anchor links
        document.addEventListener('click', (e) => {
            const anchor = e.target.closest('a[href^="#"]');
            if (anchor) {
                e.preventDefault();
                const targetId = anchor.getAttribute('href');
                
                // Skip if href is empty or just '#'
                if (!targetId || targetId === '#') {
                    e.preventDefault();
                    return;
                }
                
                const target = document.querySelector(targetId);
                
                if (target) {
                    const offset = 80; // Navbar height
                    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - offset;
                    
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                    
                    // Update focus for accessibility
                    target.setAttribute('tabindex', '-1');
                    target.focus();
                    
                    // Track navigation
                    this.analytics.trackEvent('Navigation', 'Click', anchor.textContent);
                }
            }
        });
        
        // Back to top
        backToTop?.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.analytics.trackEvent('UI', 'BackToTop', 'Click');
        });
        
        // Mobile menu
        this.setupMobileMenu();
        
        // CTA buttons
        this.setupCTAButtons();
        
        // Tabs
        this.setupTabs();
    }

    setupMobileMenu() {
        const toggle = document.querySelector('.mobile-toggle');
        const menu = document.querySelector('.nav-menu');
        
        if (toggle && menu) {
            toggle.addEventListener('click', () => {
                const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                toggle.setAttribute('aria-expanded', !isOpen);
                toggle.classList.toggle('active');
                menu.classList.toggle('active');
                
                // Prevent body scroll when menu is open
                document.body.style.overflow = !isOpen ? 'hidden' : '';
                
                this.analytics.trackEvent('UI', 'MobileMenu', isOpen ? 'Close' : 'Open');
            });
            
            // Close menu when clicking outside
            document.addEventListener('click', (e) => {
                if (!e.target.closest('.nav-container') && menu.classList.contains('active')) {
                    toggle.click();
                }
            });
            
            // Close menu on escape
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && menu.classList.contains('active')) {
                    toggle.click();
                }
            });
        }
    }

    setupCTAButtons() {
        document.querySelectorAll('.btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const action = btn.getAttribute('data-action');
                if (action) {
                    this.handleCTAClick(action, btn, e);
                }
            });
        });
    }

    handleCTAClick(action, button, event) {
        // Track CTA clicks
        this.analytics.trackEvent('CTA', action, button.textContent.trim());
        
        switch(action) {
            case 'explore':
                document.getElementById('insights')?.scrollIntoView({ behavior: 'smooth' });
                break;
            case 'learn-more':
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                break;
        }
        
        // Visual feedback
        this.createRippleEffect(button, event);
    }

    createRippleEffect(button, event) {
        const ripple = document.createElement('span');
        ripple.className = 'ripple';
        
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = event.clientX - rect.left - size / 2;
        const y = event.clientY - rect.top - size / 2;
        
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        
        button.appendChild(ripple);
        
        setTimeout(() => ripple.remove(), 600);
    }

    setupTabs() {
        const tabButtons = document.querySelectorAll('.tab-btn');
        const tabPanes = document.querySelectorAll('.tab-pane');
        
        tabButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const targetTab = btn.getAttribute('data-tab');
                
                // Update ARIA attributes
                tabButtons.forEach(b => {
                    b.classList.remove('active');
                    b.setAttribute('aria-selected', 'false');
                });
                
                tabPanes.forEach(p => {
                    p.classList.remove('active');
                    p.setAttribute('hidden', 'true');
                });
                
                btn.classList.add('active');
                btn.setAttribute('aria-selected', 'true');
                
                const targetPane = document.getElementById(`${targetTab}-panel`);
                if (targetPane) {
                    targetPane.classList.add('active');
                    targetPane.removeAttribute('hidden');
                }
                
                // Track tab views
                this.analytics.trackEvent('Content', 'TabView', targetTab);
                
                // Load content for the tab
                this.loadTabContent(targetTab);
            });
        });
        
        // Keyboard navigation
        tabButtons.forEach((btn, index) => {
            btn.addEventListener('keydown', (e) => {
                let newIndex;
                
                switch(e.key) {
                    case 'ArrowLeft':
                        newIndex = index - 1;
                        if (newIndex < 0) newIndex = tabButtons.length - 1;
                        break;
                    case 'ArrowRight':
                        newIndex = index + 1;
                        if (newIndex >= tabButtons.length) newIndex = 0;
                        break;
                    case 'Home':
                        newIndex = 0;
                        break;
                    case 'End':
                        newIndex = tabButtons.length - 1;
                        break;
                    default:
                        return;
                }
                
                e.preventDefault();
                tabButtons[newIndex].focus();
                tabButtons[newIndex].click();
            });
        });
    }

    loadTabContent(tab) {
        const container = document.querySelector(`#${tab}-panel .insight-grid`);
        if (!container) return;
        
        const content = this.getContentForTab(tab);
        
        container.innerHTML = content.map((item, index) => `
            <article class="insight-card" data-aos="fade-up" data-aos-delay="${index * 100}">
                <div class="insight-header">
                    <span class="insight-category">${this.escapeHtml(item.category)}</span>
                    <span class="insight-date">${this.escapeHtml(item.date)}</span>
                </div>
                <h4 class="insight-title">${this.escapeHtml(item.title)}</h4>
                <p class="insight-excerpt">${this.escapeHtml(item.excerpt)}</p>
                <a href="#" class="insight-link" data-insight="${this.escapeHtml(item.id)}">
                    Read more →
                </a>
            </article>
        `).join('');
        
        // Reinitialize AOS for new content
        if (window.AOS) AOS.refresh();
    }

    getContentForTab(tab) {
        // Dynamic content based on tab and domain theme
        const contentMap = {
            technology: [
                {
                    id: 'tech-1',
                    category: 'AI & ML',
                    date: 'Today',
                    title: 'The Future of Neural Networks',
                    excerpt: 'Exploring breakthrough advances in deep learning architectures and their implications for tomorrow\'s technology.'
                },
                {
                    id: 'tech-2',
                    category: 'Computing',
                    date: '2 days ago',
                    title: 'Quantum Computing Milestones',
                    excerpt: 'Recent achievements bringing quantum supremacy closer to practical reality for businesses.'
                }
            ],
            business: [
                {
                    id: 'biz-1',
                    category: 'Strategy',
                    date: 'Yesterday',
                    title: 'Digital Transformation Strategies',
                    excerpt: 'How leading companies are reshaping their business models for the digital age.'
                },
                {
                    id: 'biz-2',
                    category: 'Leadership',
                    date: '3 days ago',
                    title: 'Future-Ready Leadership',
                    excerpt: 'Essential skills and mindsets for navigating tomorrow\'s business challenges.'
                }
            ],
            innovation: [
                {
                    id: 'inno-1',
                    category: 'Research',
                    date: '3 days ago',
                    title: 'Breakthrough Innovation Methods',
                    excerpt: 'New frameworks for fostering creativity and innovation in your organization.'
                }
            ],
            future: [
                {
                    id: 'future-1',
                    category: 'Trends',
                    date: 'This week',
                    title: 'Tech Trends Shaping 2025',
                    excerpt: 'Key technologies that will define the next decade of digital transformation.'
                }
            ]
        };
        
        return contentMap[tab] || [];
    }

    initializeParticles() {
        const canvas = document.getElementById('particleCanvas');
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        const particles = [];
        const particleCount = window.innerWidth < 768 ? 25 : 50; // Fewer particles on mobile
        
        // Set canvas size
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        
        resizeCanvas();
        
        // Debounced resize
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(resizeCanvas, 250);
        });
        
        // Particle class
        class Particle {
            constructor() {
                this.reset();
            }
            
            reset() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.size = Math.random() * 3 + 1;
                this.speedX = (Math.random() - 0.5) * 0.5;
                this.speedY = (Math.random() - 0.5) * 0.5;
                this.opacity = Math.random() * 0.5 + 0.3;
            }
            
            update() {
                this.x += this.speedX;
                this.y += this.speedY;
                
                // Wrap around edges
                if (this.x < 0) this.x = canvas.width;
                if (this.x > canvas.width) this.x = 0;
                if (this.y < 0) this.y = canvas.height;
                if (this.y > canvas.height) this.y = 0;
            }
            
            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(99, 102, 241, ${this.opacity})`;
                ctx.fill();
            }
        }
        
        // Create particles
        for (let i = 0; i < particleCount; i++) {
            particles.push(new Particle());
        }
        
        // Animation loop with performance optimization
        let animationId;
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            particles.forEach(particle => {
                particle.update();
                particle.draw();
            });
            
            // Draw connections
            particles.forEach((p1, i) => {
                particles.slice(i + 1).forEach(p2 => {
                    const distance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
                    
                    if (distance < 150) {
                        ctx.beginPath();
                        ctx.moveTo(p1.x, p1.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.strokeStyle = `rgba(99, 102, 241, ${0.2 * (1 - distance / 150)})`;
                        ctx.stroke();
                    }
                });
            });
            
            animationId = requestAnimationFrame(animate);
        };
        
        animate();
        
        // Pause animation when page is hidden
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                cancelAnimationFrame(animationId);
            } else {
                animate();
            }
        });
    }

    setupIntersectionObservers() {
        // Parallax effect with performance optimization
        const heroContent = document.querySelector('.hero-content');
        
        if (heroContent) {
            let ticking = false;
            
            const updateParallax = () => {
                const scrolled = window.pageYOffset;
                
                if (scrolled < 800) {
                    const rate = scrolled * -0.3;
                    heroContent.style.transform = `translateY(${rate}px)`;
                }
                
                ticking = false;
            };
            
            window.addEventListener('scroll', () => {
                if (!ticking) {
                    window.requestAnimationFrame(updateParallax);
                    ticking = true;
                }
            }, { passive: true });
        }
        
        // Reveal animations with stagger
        const revealElements = document.querySelectorAll('.feature-card, .stat-card');
        
        const revealObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry, index) => {
                if (entry.isIntersecting) {
                    setTimeout(() => {
                        entry.target.classList.add('revealed');
                    }, index * 100);
                    revealObserver.unobserve(entry.target);
                }
            });
        }, { 
            threshold: 0.1,
            rootMargin: '50px'
        });
        
        revealElements.forEach(el => revealObserver.observe(el));
    }

    initializeDynamicContent() {
        // Load content based on domain configuration
        const sections = this.config.primaryContent?.sections || [];
        
        // Populate features
        const featuresGrid = document.querySelector('.features-grid');
        if (featuresGrid && sections.length > 0) {
            featuresGrid.innerHTML = sections.slice(0, 3).map((section, index) => `
                <article class="feature-card" data-aos="fade-up" data-aos-delay="${(index + 1) * 100}">
                    <div class="feature-icon" aria-hidden="true">
                        ${this.getIconForSection(section.title)}
                    </div>
                    <h3>${this.escapeHtml(section.title)}</h3>
                    <p>${this.escapeHtml(section.content)}</p>
                </article>
            `).join('');
        }
    }

    getIconForSection(title) {
        const iconMap = {
            'AI': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>',
            'Technology': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
            'Innovation': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>',
            'default': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M9 11H3v2h6v-2zm0-4H3v2h6V7zm0 8H3v2h6v-2zm12-4h-6v2h6v-2zm0-4h-6v2h6V7zm0 8h-6v2h6v-2z"/></svg>'
        };
        
        for (const [key, icon] of Object.entries(iconMap)) {
            if (title.includes(key)) return icon;
        }
        
        return iconMap.default;
    }

    initializeCookieConsent() {
        const consent = localStorage.getItem('cookieConsent');
        const cookieBanner = document.getElementById('cookieConsent');
        
        if (!consent && cookieBanner) {
            // Show cookie banner after delay
            setTimeout(() => {
                cookieBanner.classList.add('show');
                cookieBanner.setAttribute('aria-hidden', 'false');
            }, 2000);
            
            document.getElementById('cookieAccept')?.addEventListener('click', () => {
                this.handleCookieConsent(true, cookieBanner);
            });
            
            document.getElementById('cookieDecline')?.addEventListener('click', () => {
                this.handleCookieConsent(false, cookieBanner);
            });
        }
    }

    handleCookieConsent(accepted, banner) {
        localStorage.setItem('cookieConsent', accepted ? 'accepted' : 'declined');
        banner.classList.remove('show');
        banner.setAttribute('aria-hidden', 'true');
        
        if (accepted) {
            // Enable full tracking
            this.analytics.enableFullTracking();
        } else {
            // Enable limited tracking
            this.analytics.enableLimitedTracking();
        }
        
        // Track consent
        this.analytics.trackEvent('Privacy', 'CookieConsent', accepted ? 'Accept' : 'Decline');
    }

    handleInitError(error) {
        console.error('Failed to initialize site:', error);
        
        // Remove preloader on error
        const preloader = document.getElementById('preloader');
        if (preloader) {
            preloader.classList.add('loaded');
        }
        
        // Show error notification if possible
        const notification = document.createElement('div');
        notification.className = 'notification notification-error';
        notification.innerHTML = `
            <div class="notification-content">
                <p>There was an error loading the site. Please refresh the page.</p>
            </div>
        `;
        document.body.appendChild(notification);
    }
}

// Make analytics available globally for compatibility
window.analytics = {
    trackEvent: (category, action, label) => {
        if (window.modernSite && window.modernSite.analytics) {
            window.modernSite.analytics.trackEvent(category, action, label);
        }
    },
    getTimeOnPage: () => {
        if (window.modernSite && window.modernSite.analytics) {
            return window.modernSite.analytics.getTimeOnPage();
        }
        return 0;
    },
    getCurrentScrollDepth: () => {
        if (window.modernSite && window.modernSite.analytics) {
            return window.modernSite.analytics.getCurrentScrollDepth();
        }
        return 0;
    },
    determineVisitorIntent: () => {
        if (window.modernSite && window.modernSite.analytics) {
            return window.modernSite.analytics.determineVisitorIntent();
        }
        return 'unknown';
    },
    sessionId: Date.now().toString()
};

// Initialize the site when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.modernSite = new ModernSite();
    });
} else {
    window.modernSite = new ModernSite();
}

// Export for use in other modules
export { ModernSite };